/**
 * ContactDetailPageCore
 *
 * Pure presentational component for the contact detail page.
 * Works in both Next.js (with tRPC) and Electron (with IPC) by receiving
 * all data and callbacks via props. Uses render props for platform-specific components.
 */
import type { ComponentType, ReactNode } from 'react';
import { useState } from 'react';

import type { VoiceRecorderProps } from '../VoiceRecorder';

import type { Stage } from '../Input2/StageButton';
import type { Cadence, CadenceStep, Contact, ContactDossier, ContactUser, ContactUserInteraction, DossierFailure, Org, Step } from './types';
import { cn } from '../cn';
import { ContactDetailTabs } from './ContactDetailTabs';
import { ContactInteractSectionPure } from './ContactInteractSectionPure';
import type { CadenceData } from './ContactInteractSectionPure';
import type {
  AIMessageSectionData,
  StepCreateInput as CadenceStepCreateInput,
  StepUpdateInput as CadenceStepUpdateInput,
  TemplateInfo,
  DocumentData,
  CadenceGraph,
} from './CadenceSectionPure';
import {
  ContactOneOffActionSectionPure,
  type ActionStepConfig,
  type PendingStep,
  type ScheduleStatus,
  type ActionStep,
  type SendMode,
  type StepType,
  type ActionSectionUserTeam,
  type StepEditorData,
  type StepEditorCallbacks,
  type ReminderCardData,
  type ReminderCardCallbacks,
  type VoiceNoteCardData,
  type VoiceNoteCardCallbacks,
} from './ContactOneOffActionSectionPure';
import type { SiftConfig } from '../CGStep';
import DetailHeader from './DetailHeader';
import DetailHeaderContactPure from './DetailHeaderContactPure';
import Button from '../Input2/Button';
import StageButton from '../Input2/StageButton';
import { Arrows } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import type {
  ContactDetailMutations,
  HistoryData,
  MeetingBookedData,
  PendingActionData,
  TeamUserOption,
} from './types';
import { ContactDataSectionPure } from './ContactDataSectionPure';
import { ContactDossierSection } from './ContactDossierSection';
import type {
  ContactExperienceItem,
  ContactEducationItem,
} from './ContactEmploymentEducationSectionPure';
import type { AddExperienceInput, OrgSearchOption } from './AddExperiencePopup';
import type { ContactSignalGridItem } from './ContactSignalsGrid';
import type { LinkedInMapping } from '../LinkedInIdentifiersSection';
import { SidebarNotes } from '../SidebarNotes';
import { AlertLabel } from '../AlertLabel';
import { InfoBox } from '../InfoBox';
import { Scrollable, SmallPadded } from '../Scrollable';
import { General, Users } from '../StrokeIcons';
import CampaignButtonWithCadence, {
  type TemplateOption,
  type OwnerOption,
  type StagingCandidate,
} from '../Input2/CampaignButtonWithCadence';
import { ResponseSection } from '../ResponseSection';
import {
  CadenceStepSectionPure,
  type StepData,
  type StepChanges,
  type AiChoice,
  type EditorDocument,
  type StepListItem,
} from '../CadenceStep';

// ============================================================================
// Types
// ============================================================================

export type TabId = 'action' | 'interact' | 'data' | 'dossier';

/**
 * Cadence data from the contact query
 */
export type ContactActiveCadence = {
  cadence: Cadence & { state?: string; templateId?: string | null; nextStepTypes?: string[]; nextRunTime?: Date | string | null; nextStepBody?: string | null; nextStepSubject?: string | null };
  cadenceSteps: (CadenceStep & { errorMessage?: string | null })[] | null;
  cadenceContactUser?: ContactUser | null;
};

/**
 * Full contact detail data from the contact.byId query
 */
export type ContactDetailData = {
  contactV2: Contact;
  team: { id: string; name: string };
  authedUser: ContactUser;
  contactOrg: (Org & { primaryFields?: { name?: { data?: string } }; primaryPictureUrl?: string | null }) | null;
  responses: ContactUserInteraction[];
  activeCadence: ContactActiveCadence | null;
};

/**
 * Data for pure action section component
 */
export type ActionSectionData = {
  userTeam: ActionSectionUserTeam;
  cadenceId?: string | null;
  pendingStep?: PendingStep | null;
  scheduleStatus?: ScheduleStatus | null;
  linkedinSteps: ActionStepConfig[];
  otherSteps: ActionStepConfig[];
  currentAction?: string;
  isAddingToTakeAction?: boolean;
  isDeletingPendingStep?: boolean;
  isUnipileConnecting?: boolean;
  isSubmitting?: boolean;
  atVariables?: Record<string, string>;
  superAdminAiPreview?: {
    onGenerate: () => void;
    isGenerating: boolean;
    prompt: string | null;
    message: string | null;
    error?: string | null;
    instructions: string;
    onChangeInstructions: (value: string) => void;
    shouldSend?: { shouldSend: boolean; reasoning: string } | null;
  };
};

/**
 * Retry state for attachment processing
 */
export type RetryState = 'idle' | 'pending' | 'success' | 'error';

export type ContactDetailPageCoreProps = {
  // Contact identifier (available even before data loads)
  contactId: string;

  authTeamId: string;
  authTeamName: string;
  authUserId: string;
  authUnipileAccountId?: string;
  authUnipileAccountError?: string | null;
  authIsPremium?: boolean;
  authCreditsRemaining: number;

  // Data
  data: ContactDetailData | null;
  isLoading: boolean;

  // Supplementary data
  stages: Stage[];
  stagesLoading: boolean;
  history: HistoryData;
  historyIsLoading: boolean;
  steps: Step[];
  stepsIsLoading: boolean;
  meetingBookedDetails: MeetingBookedData[] | null;
  pendingAction?: PendingActionData | null;
  teamUsers?: TeamUserOption[];

  // Tab state (can be controlled externally or internally)
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;

  // All mutations as callbacks
  mutations: ContactDetailMutations;

  // Campaign button data
  campaignTemplates: TemplateOption[];
  campaignOwners: OwnerOption[];
  lastUsedTemplateId?: string | null;
  lastUsedTemplateName?: string | null;
  stagingCandidates?: StagingCandidate[];
  onStagingClick?: (candidate: StagingCandidate) => void;
  onStagingDelete?: (candidate: StagingCandidate) => Promise<void>;
  isStagingDeletingCandidateId?: number | null;
  /** Reassign a staging candidate's owner from the campaign-badge avatar dropdown. */
  onStagingChangeOwner?: (candidate: StagingCandidate, newUserId: string) => Promise<void>;
  /** Forwarded into ContactInteractSectionPure's candidate-in-progress card. */
  onStagingRetry?: (candidate: StagingCandidate) => Promise<unknown>;
  stagingRetryingCandidateId?: number | null;

  // Action section data (pure approach - preferred)
  actionSectionData?: ActionSectionData | null;

  // Action section callbacks (pure approach)
  onActionChange?: (action: string | undefined) => void;
  onSubmitActionStep?: (step: ActionStep, sendMode: SendMode) => Promise<void>;
  onAddToTakeAction?: (onlyNewPeople: boolean) => Promise<void>;
  addToTakeActionOnlyNewPeople?: boolean;
  onChangeAddToTakeActionOnlyNewPeople?: (onlyNewPeople: boolean) => void;
  onDeletePendingStep?: () => void;
  onUnipileConnect?: () => void;
  getInitialActionStep?: (type: StepType) => ActionStep;

  // Step editor data (pure approach - renders CadenceStepSectionPure directly)
  stepEditorData?: StepEditorData;
  stepEditorCallbacks?: StepEditorCallbacks;
  autoGenerateAi?: boolean;
  onAutoGenerateAiChange?: (value: boolean) => void;

  // Action card props
  /** Whether the current platform is macOS-like (for facetime button) */
  isMacLike?: boolean;
  /** Phone number for call step type */
  tel?: string | null;
  /** Callback when manual call is recorded (for tel: links) */
  onRecordManualCall?: () => void;
  /** Injected in-app click-to-call trigger; shared by dashboard + extension surfaces. */
  clickToCall?: ReactNode;
  /**
   * Injected dialer factory for a `call`-type cadence reminder. Returns the surface's
   * click-to-call button wired to the given step id, so logging the outcome completes the step.
   */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => ReactNode;
  /** Completes a `call`-type cadence reminder the rep handled outside the dialer; shared by dashboard + extension. */
  onMarkCallStepDone?: (args: { cadenceStepId: string; outcome: string; notes?: string }) => Promise<void> | void;

  // Reminder card (pure approach)
  reminderCardData?: ReminderCardData;
  reminderCardCallbacks?: ReminderCardCallbacks;

  // Voice note card (pure approach)
  voiceNoteCardData?: VoiceNoteCardData;
  voiceNoteCardCallbacks?: VoiceNoteCardCallbacks;
  /** Optional replacement for the built-in `VoiceRecorder` in the voice-note card.
      Used by the Chrome extension to route mic capture through an offscreen document. */
  voiceRecorderComponent?: ComponentType<VoiceRecorderProps>;

  // Cadence section data
  cadenceStepsGraph?: CadenceGraph | null;
  cadenceTemplateInfo?: TemplateInfo | null;
  cadenceDocuments?: DocumentData[];

  // Cadence section callbacks
  onCadenceCreateStep?: (data: CadenceStepCreateInput) => unknown;
  onCadenceUpdateStep?: (stepId: string, data: CadenceStepUpdateInput) => unknown;
  onCadenceDeleteStep?: (stepId: string) => unknown;
  onCadenceChangeStepType?: (stepId: string, type: string) => unknown;
  onCadenceMarkStepFinished?: (stepId: string) => unknown;
  onToggleInmailOpen?: (nodeId: string, onlyOpenLink: boolean) => unknown;

  // Smart-campaign AI Message Section removed; kept as a no-op placeholder.
  aiMessageData?: AIMessageSectionData | null;

  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => unknown;
  }) => ReactNode;

  // Reminder editor — list of available reminder actions for the team and a
  // create-on-the-fly callback. Threaded straight through to the cadence
  // card's inline reminder editor.
  reminderActions?: { id: string; name: string }[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;

  // Toast callbacks for field copy operations
  onFieldCopied?: (data: string) => unknown;
  onFieldCopyFailed?: (data: string) => unknown;

  // UI options
  onClose?: () => unknown;
  onClickOrg?: () => unknown;
  hideCloseButton?: boolean;
  hideActionTab?: boolean;
  hideDossierTab?: boolean;
  honorificAgeThreshold?: number | null;
  className?: string;

  // Superadmin features
  onCopyToSuperadminTeam?: () => unknown;
  isCopyPending?: boolean;
  superadminName?: string;
  superadminTeamName?: string;

  // LinkedIn Identifiers section data and callbacks
  linkedInMappings: LinkedInMapping[];
  linkedinUnavailable?: boolean;
  isLinkedInMappingsLoading: boolean;
  onDeleteLinkedInMapping: (mappingId: number) => unknown;
  onVerifyLinkedInMapping: (mappingId: number) => unknown;
  onCopyLinkedInMapping: (foreignId: string) => unknown;
  onAddLinkedInMapping: (foreignSource: 'linkedin' | 'salesnav', foreignId: string) => void;
  isLinkedInMappingDeleting: boolean;
  isLinkedInMappingVerifying: boolean;
  isLinkedInMappingAdding: boolean;

  // Contact tags render prop
  renderContactTags?: () => ReactNode;

  // Signals (rendered as a 3x3 grid just below the Track LinkedIn toggle when signals is provided)
  signals?: ContactSignalGridItem[];
  isSignalsLoading?: boolean;
  lastUnipileFetchAt?: Date | string | null;
  ignoredAspectKeys?: ReadonlySet<string>;

  // Dossier — sits next to Signals as the second "research/enrichment"
  // subsection. Null when no dossier exists; isDossierGenerating drives the
  // spinner UI for the in-progress case.
  dossier?: ContactDossier | null;
  isDossierGenerating?: boolean;
  hasTavilyKey?: boolean;
  onSaveTavilyKey?: (key: string) => Promise<unknown>;
  onRegenerateDossier?: () => Promise<unknown>;
  isRegeneratingDossier?: boolean;
  // The most recent terminally-failed generation attempt, or null — drives the
  // detailed error state on the Dossier tab.
  dossierFailure?: DossierFailure | null;

  // Employment & Education history (data tab)
  experiences?: ContactExperienceItem[];
  education?: ContactEducationItem[];
  isHistoryLoading?: boolean;
  onAddExperience?: (input: AddExperienceInput) => Promise<void>;
  orgSearch?: (query: string) => Promise<OrgSearchOption[]>;
  onDeleteExperience?: (experienceId: string) => Promise<void>;

  // View campaigns callback
  onViewAllCampaigns?: () => void;

  // View required actions callback
  onViewRequiredActions?: () => void;

  // Click cadence in history to view it
  onClickCadence?: (cadenceId: string) => void;

  // Team context for warnings
  contactTeamId?: string;
  contactTeamName?: string;

  // Respond step section
  respondStep?: StepData | null;
  onCancelRespond?: () => void;
  onClickToRespond?: (type: string) => void;
  // Respond section data
  respondDocuments?: EditorDocument[];
  respondAiChoices?: AiChoice[];
  respondAiIndex?: number;
  respondAiCommand?: string;
  respondAiLoading?: boolean;
  respondStepsList?: StepListItem[];
  respondIsSubmitting?: boolean;
  respondIsAiGenerating?: boolean;
  // Respond section callbacks
  onRespondSubmit?: (step: StepData) => Promise<void>;
  onRespondChange?: (changes: StepChanges) => void;
  onRespondGenerateAi?: (empty?: boolean) => Promise<void>;
  onRespondAiCommandChange?: (command: string) => void;
  onRespondAiIndexChange?: (index: number) => void;
  onRespondToast?: (message: string, type: 'error' | 'success') => void;

  // Step tracking (for CadenceSection highlighting)
  updatedStepId?: string;
  setUpdatedStepId?: (id: string | undefined) => void;

  // Attachment retry states
  retryStates?: Record<string, RetryState>;
  retryErrors?: Record<string, string | null>;
  onRetryAttachment?: (interactionId: string) => void;

  // Transcription request states
  transcriptionRequestStates?: Record<string, 'idle' | 'pending' | 'success' | 'error'>;
  onRequestTranscription?: (interactionId: string) => void;

  // @variables for message interpolation
  atVariables?: Record<string, string>;

  // Org navigation
  orgFirst?: boolean;
  onOrgNavigate?: (orgId: string) => void;

  // Additional buttons to render in the header (next to stage button)
  additionalHeaderButtons?: ReactNode;
  voiceEnabled?: boolean;
  voiceCallingEnabled?: boolean;
  /** Origin for the "See in template" link — see CadenceSectionPureProps. */
  templateLinkBaseUrl?: string;
};

// ============================================================================
// ContactDetailPageCore
// ============================================================================

export function ContactDetailPageCore({
  contactId,
  authTeamId,
  authTeamName,
  authUserId,
  authIsPremium,
  authUnipileAccountId,
  authUnipileAccountError,
  authCreditsRemaining,
  data,
  isLoading,
  stages,
  stagesLoading,
  history,
  historyIsLoading,
  steps,
  stepsIsLoading,
  meetingBookedDetails,
  pendingAction,
  activeTab: activeTabProp,
  onTabChange,
  mutations,
  campaignTemplates,
  campaignOwners,
  lastUsedTemplateId,
  lastUsedTemplateName,
  stagingCandidates,
  onStagingClick,
  onStagingDelete,
  isStagingDeletingCandidateId,
  onStagingChangeOwner,
  onStagingRetry,
  stagingRetryingCandidateId,
  actionSectionData,
  onActionChange,
  onSubmitActionStep,
  onAddToTakeAction,
  addToTakeActionOnlyNewPeople = true,
  onChangeAddToTakeActionOnlyNewPeople,
  onDeletePendingStep,
  onUnipileConnect,
  getInitialActionStep,
  stepEditorData,
  stepEditorCallbacks,
  autoGenerateAi,
  onAutoGenerateAiChange,
  isMacLike,
  tel,
  onRecordManualCall,
  clickToCall,
  renderCadenceCallButton,
  onMarkCallStepDone,
  reminderCardData,
  reminderCardCallbacks,
  voiceNoteCardData,
  voiceNoteCardCallbacks,
  voiceRecorderComponent,
  cadenceStepsGraph,
  cadenceTemplateInfo,
  cadenceDocuments,
  onCadenceCreateStep,
  onCadenceUpdateStep,
  onCadenceDeleteStep,
  onCadenceChangeStepType,
  onCadenceMarkStepFinished,
  onToggleInmailOpen,
  aiMessageData: _aiMessageData,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  onFieldCopied,
  onFieldCopyFailed,
  onClose,
  onClickOrg,
  hideCloseButton,
  hideActionTab,
  hideDossierTab,
  honorificAgeThreshold,
  className,
  onCopyToSuperadminTeam,
  isCopyPending,
  superadminName,
  superadminTeamName,
  linkedInMappings,
  linkedinUnavailable,
  isLinkedInMappingsLoading,
  onDeleteLinkedInMapping,
  onVerifyLinkedInMapping,
  onCopyLinkedInMapping,
  onAddLinkedInMapping,
  isLinkedInMappingDeleting,
  isLinkedInMappingVerifying,
  isLinkedInMappingAdding,
  renderContactTags,
  signals,
  isSignalsLoading,
  lastUnipileFetchAt,
  ignoredAspectKeys,
  dossier = null,
  isDossierGenerating = false,
  dossierFailure = null,
  hasTavilyKey = false,
  onSaveTavilyKey,
  onRegenerateDossier,
  isRegeneratingDossier = false,
  experiences,
  education,
  isHistoryLoading,
  onAddExperience,
  orgSearch,
  onDeleteExperience,
  onViewAllCampaigns,
  onViewRequiredActions,
  onClickCadence,
  contactTeamId,
  contactTeamName,
  respondStep,
  onCancelRespond,
  onClickToRespond,
  respondDocuments,
  respondAiChoices,
  respondAiIndex,
  respondAiCommand,
  respondAiLoading,
  respondStepsList,
  respondIsSubmitting,
  respondIsAiGenerating,
  onRespondSubmit,
  onRespondChange,
  onRespondGenerateAi,
  onRespondAiCommandChange,
  onRespondAiIndexChange,
  onRespondToast,
  updatedStepId: _updatedStepId,
  setUpdatedStepId: _setUpdatedStepId,
  retryStates,
  retryErrors,
  onRetryAttachment: _onRetryAttachment,
  transcriptionRequestStates,
  onRequestTranscription,
  atVariables: atVariablesProp,
  orgFirst: _orgFirst,
  onOrgNavigate: _onOrgNavigate,
  teamUsers: _teamUsers,
  additionalHeaderButtons,
  voiceEnabled,
  voiceCallingEnabled,
  templateLinkBaseUrl,
}: ContactDetailPageCoreProps) {
  // Internal tab state (if not controlled)
  const [internalTab, setInternalTab] = useState<TabId>('interact');
  const activeTab = activeTabProp ?? internalTab;
  const handleTabChange = (tab: TabId) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalTab(tab);
    }
  };

  // Notes state
  const [notesOpen, setNotesOpen] = useState(false);

  // Response dismiss state
  const [isAckPending, setIsAckPending] = useState(false);

  // Team mismatch check
  const isTeamMismatch = contactTeamId != null && contactTeamId !== authTeamId;

  // ============================================================================
  // Loading / not-found are handled by separate components — see
  // `ContactDetailPageSkeleton` and `ContactDetailPageNotFound`. The host
  // is expected to render those when `isLoading` is true or `data` is
  // `null`, so by this point we can assume we have real data to show.
  // ============================================================================
  if (isLoading || data == null) {
    // Defensive guard so existing callers that haven't migrated yet
    // don't crash — but ALL new code should switch to the skeleton /
    // not-found components.
    return null;
  }

  // ============================================================================
  // Extract Data
  // ============================================================================
  const contact = data.contactV2;
  // Note: data.team available for team mismatch warnings
  const contactOrg = data.contactOrg;
  const responses = data.responses ?? [];
  const activeCadence = data.activeCadence;

  const contactResponse = responses[0];
  const notes = contact.notes;
  const stepError = activeCadence?.cadenceSteps?.find((s) => s.errorMessage)?.errorMessage;
  const hasNotes = notes != null && notes.length > 0 && notes !== '<p></p>' && notes !== '<p><br></p>';
  const hasSteps = steps.length > 0;
  const hasCadence = activeCadence?.cadence != null;

  // Contact name and display info
  const contactName = [contact.primaryFields?.firstName?.data, contact.primaryFields?.lastName?.data]
    .filter(Boolean)
    .join(' ');
  const avatarUrl = contact.primaryPictureUrl;
  const headline = contact.primaryFields?.headline?.data;
  const title = contact.primaryFields?.title?.data;
  const linkedinUsername = contact.liUsername ?? contact.liSalesNavId;
  const hasValidLinkedInUrl =
    linkedinUsername != null && linkedinUsername !== 'null' && linkedinUsername !== 'undefined';
  const linkedinUrl = hasValidLinkedInUrl ? `https://www.linkedin.com/in/${linkedinUsername}/` : undefined;

  // Age and honorific display
  // Use displayHonorific from server if available (calculated based on age threshold), otherwise calculate locally
  const minAgeEstimate = contact.ageEstimate?.minAge;
  const serverDisplayHonorific = (contact as { displayHonorific?: boolean }).displayHonorific;
  const displayHonorific = serverDisplayHonorific ?? (honorificAgeThreshold != null && minAgeEstimate != null && minAgeEstimate >= honorificAgeThreshold);

  // Build cadence data for ContactInteractSectionPure
  const cadenceData: CadenceData | undefined = activeCadence
    ? {
        cadence: activeCadence.cadence,
        cadenceSteps: activeCadence.cadenceSteps,
      }
    : undefined;

  // At variables from parent
  const atVariables = atVariablesProp ?? {};

  // Notes change handler
  const handleNotesChange = async (newNotes: string) => {
    await mutations.updateNotes(newNotes);
  };

  // Response dismiss handler
  const handleDismissResponse = async () => {
    setIsAckPending(true);
    try {
      await mutations.ackResponse(contactId, true);
    } finally {
      setIsAckPending(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={cn('flex h-full flex-col overflow-hidden', className)}>
      {/* Team mismatch warning */}
      {isTeamMismatch && contactTeamName && (
        <SmallPadded>
          <AlertLabel size="sm" type="warning" icon={<General.InfoCircle />}>
            Contact is part of &rsquo;{contactTeamName}&lsquo; team, your currently selected team is &rsquo;
            {authTeamName}&lsquo;
          </AlertLabel>
        </SmallPadded>
      )}

      {/* Response section (if contact has a response) */}
      {contactResponse != null && (
        <ResponseSection
          contactFirstName={contact.primaryFields?.firstName?.data}
          cadenceState={activeCadence?.cadence?.state}
          responseAction={contactResponse.interactionType}
          responseDate={contactResponse.createdAt}
          responseSubject={contactResponse.subject}
          responseBody={contactResponse.body}
          onDismissResponse={handleDismissResponse}
          isLoading={isAckPending}
          onRespond={() => onClickToRespond?.(contactResponse.interactionType)}
        />
      )}

      {/* Header */}
      <DetailHeader
        isPaused={activeCadence?.cadence?.state === 'paused'}
        className="bg-slate-50 dark:bg-slate-900"
        onClose={onClose}
        hideCloseButton={hideCloseButton}
        buttons={
          <>
            <StageButton
              size="xs"
              stages={stages}
              stageId={contact.stageId ?? undefined}
              disabled={stagesLoading}
              onChange={mutations.setContactStage}
              status={mutations.isSettingContactStage ? 'updating' : undefined}
              icon={<Users.Users01 />}
            />
            {additionalHeaderButtons}
          </>
        }
        footer={
          <div className="flex flex-col px-4 py-2">
            <div className="flex items-center justify-end gap-2 pb-5">
              <CampaignButtonWithCadence
                cadence={
                  activeCadence?.cadence
                    ? {
                        id: activeCadence.cadence.id,
                        state: activeCadence.cadence.state ?? 'not-started',
                        templateId: activeCadence.cadence.templateId ?? null,
                        name: activeCadence.cadence.name ?? '',
                        interruptMessage: null,
                        interruptReason: null,
                      }
                    : undefined
                }
                cadenceUserId={activeCadence?.cadenceContactUser?.userId}
                cadenceOwnerName={activeCadence?.cadenceContactUser?.name}
                cadenceOwnerProfilePicture={activeCadence?.cadenceContactUser?.profilePicture}
                currentUserId={authUserId}
                templates={campaignTemplates}
                owners={campaignOwners}
                lastUsedTemplateId={lastUsedTemplateId}
                lastUsedTemplateName={lastUsedTemplateName}
                stepError={stepError}
                onEnroll={mutations.enrollInCampaign}
                onPause={mutations.pauseCadence}
                onResume={mutations.resumeCadence}
                onReattach={mutations.reattachCadence}
                onDelete={mutations.deleteCadence}
                onChangeOwner={mutations.changeCadenceOwner}
                isPending={mutations.isCampaignPending}
                isDeleting={mutations.isCampaignDeleting}
                stagingCandidates={stagingCandidates}
                onStagingClick={onStagingClick}
                onStagingDelete={onStagingDelete}
                isStagingDeletingCandidateId={isStagingDeletingCandidateId}
                onStagingChangeOwner={onStagingChangeOwner}
              />
            </div>
            <ContactDetailTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              hasNotes={hasNotes}
              notesOpen={notesOpen}
              onToggleNotes={() => setNotesOpen((o) => !o)}
              hideActionTab={hideActionTab}
              hideDossierTab={hideDossierTab}
              rightAction={(() => {
                const hasUnipileAccount = authUnipileAccountId != null;
                const liDisconnected = hasUnipileAccount && authUnipileAccountError != null;
                if (hasUnipileAccount && !liDisconnected) {
                  return (
                    <Button
                      size="sm"
                      shape="pill"
                      color="blue" tone="outline"
                      disabled={
                        mutations.isRefreshing ||
                        mutations.isEnhancing ||
                        !(contact.liUsername || contact.liSalesNavId)
                      }
                      icon={
                        mutations.isRefreshing || mutations.isEnhancing ? (
                          <WaveSpinner />
                        ) : (
                          <Arrows.RefreshCw04 />
                        )
                      }
                      onClick={async () => {
                        await mutations.refreshFromLinkedIn();
                        if ((authIsPremium ?? false) && authCreditsRemaining > 0) {
                          await mutations.enhanceContact();
                        }
                      }}
                      tooltip={
                        <span className="block">
                          <span className="block">
                            {(authIsPremium ?? false) && authCreditsRemaining > 0
                              ? `Refresh & enrich (uses 1 of ${authCreditsRemaining.toLocaleString()} credits)`
                              : 'Refresh LinkedIn data only — no enrichment credits remaining'}
                          </span>
                          <span className="mt-0.5 block opacity-70">
                            {lastUnipileFetchAt != null
                              ? `Last LinkedIn fetch: ${new Date(lastUnipileFetchAt).toLocaleString()}`
                              : 'Never fetched from LinkedIn'}
                          </span>
                        </span>
                      }
                    />
                  );
                }
                if (liDisconnected && onUnipileConnect) {
                  return (
                    <Button
                      size="sm"
                      shape="pill"
                      color="yellow" tone="outline"
                      icon={<Arrows.RefreshCw04 />}
                      onClick={onUnipileConnect}
                      tooltip="LinkedIn account disconnected — click to reconnect and refresh"
                    />
                  );
                }
                if (onUnipileConnect) {
                  return (
                    <Button
                      size="sm"
                      shape="pill"
                      color="yellow" tone="outline"
                      icon={<General.Link01 />}
                      onClick={onUnipileConnect}
                      tooltip="Connect your LinkedIn account via API to enable data refresh"
                    />
                  );
                }
                return null;
              })()}
            />
          </div>
        }>
        <DetailHeaderContactPure
          name={contactName}
          // Mirror the search-card behavior in ContactCardImage: pass the
          // contact photo first, then the org logo as a fallback. Avatar
          // walks the list on image-load errors and uses initials only when
          // every entry fails. Without this fallback, LinkedIn signed URLs
          // that have expired (the `e=…` timestamp) drop the avatar to JB-
          // style initials while the card still shows the org badge.
          avatarUrls={[avatarUrl, contactOrg?.primaryPictureUrl]}
          linkedinUrl={linkedinUrl}
          subtitle={title ?? headline}
          orgName={contactOrg?.primaryFields?.name?.data}
          orgAvatarUrl={contactOrg?.primaryPictureUrl}
          onClickOrg={onClickOrg}
          isConnected={data.authedUser?.liConnected ?? undefined}
          displayHonorific={displayHonorific}
          honorificTitle={contact.primaryFields?.honorificTitle?.data}
          cadenceContactUser={
            activeCadence?.cadenceContactUser
              ? {
                  userId: activeCadence.cadenceContactUser.userId,
                  profilePicture: activeCadence.cadenceContactUser.profilePicture,
                  userTeamName: activeCadence.cadenceContactUser.teamName,
                  userEmail: activeCadence.cadenceContactUser.email ?? '',
                }
              : undefined
          }
        />
      </DetailHeader>

      {/* Content area */}
      <div className="relative flex h-0 w-full grow flex-col overflow-hidden">
        {notesOpen && (
          <SidebarNotes
            recordId={contactId}
            title="Notes"
            initialValue={notes ?? ''}
            onDebounceChange={handleNotesChange}
          />
        )}

        {/* Action Tab */}
        {activeTab === 'action' && isTeamMismatch && contactTeamName && (
          <SmallPadded>
            <InfoBox title="Actions disabled">
              <div>switch to &apos;{contactTeamName}&apos; team to perform actions</div>
            </InfoBox>
          </SmallPadded>
        )}
        {/* Pure action section (preferred) */}
        {activeTab === 'action' && !isTeamMismatch && actionSectionData && onActionChange && onAddToTakeAction && (
          <ContactOneOffActionSectionPure
            key={contactId}
            contactId={contactId}
            contactPrimaryFields={contact.primaryFields}
            liConnectedAt={data.authedUser.liConnectedAt}
            emailCredentialId={data.authedUser.emailCredentialId}
            userTeam={actionSectionData.userTeam}
            cadenceId={actionSectionData.cadenceId}
            pendingStep={actionSectionData.pendingStep}
            scheduleStatus={actionSectionData.scheduleStatus}
            linkedinSteps={actionSectionData.linkedinSteps}
            otherSteps={actionSectionData.otherSteps}
            currentAction={actionSectionData.currentAction}
            onActionChange={onActionChange}
            onSubmitStep={onSubmitActionStep ?? (async () => {})}
            onAddToTakeAction={onAddToTakeAction}
            addToTakeActionOnlyNewPeople={addToTakeActionOnlyNewPeople}
            onChangeAddToTakeActionOnlyNewPeople={onChangeAddToTakeActionOnlyNewPeople ?? (() => {})}
            onDeletePendingStep={onDeletePendingStep}
            isDeletingPendingStep={actionSectionData.isDeletingPendingStep}
            onUnipileConnect={onUnipileConnect}
            isUnipileConnecting={actionSectionData.isUnipileConnecting}
            isAddingToTakeAction={actionSectionData.isAddingToTakeAction}
            isSubmitting={actionSectionData.isSubmitting}
            atVariables={actionSectionData.atVariables}
            getInitialStep={getInitialActionStep}
            // Step editor data (pure approach)
            stepEditorData={stepEditorData}
            stepEditorCallbacks={stepEditorCallbacks}
            autoGenerateAi={autoGenerateAi}
            onAutoGenerateAiChange={onAutoGenerateAiChange}
            // Action card props
            isMacLike={isMacLike}
            tel={tel}
            onRecordManualCall={onRecordManualCall}
            clickToCall={clickToCall}
            renderCadenceCallButton={renderCadenceCallButton}
            onMarkCallStepDone={onMarkCallStepDone}
            reminderCardData={reminderCardData}
            reminderCardCallbacks={reminderCardCallbacks}
            voiceNoteCardData={voiceNoteCardData}
            voiceNoteCardCallbacks={voiceNoteCardCallbacks}
            voiceRecorderComponent={voiceRecorderComponent}
            superAdminAiPreview={actionSectionData.superAdminAiPreview}
          />
        )}

        {/* Interact Tab */}
        {activeTab === 'interact' && (
          <ContactInteractSectionPure
            currentUserId={authUserId}
            contactId={contactId}
            contactPrimaryFields={contact.primaryFields}
            contactLiAbout={contact.liAbout}
            contactAlreadyConnectedOnLinkedIn={data.authedUser?.liConnectedAt != null}
            cadenceData={cadenceData}
            stagingCandidates={stagingCandidates}
            onStagingClick={onStagingClick}
            onStagingRetry={onStagingRetry}
            stagingRetryingCandidateId={stagingRetryingCandidateId}
            org={contactOrg ?? undefined}
            history={history}
            historyIsLoading={historyIsLoading}
            stepsIsLoading={stepsIsLoading}
            onClickToRespond={onClickToRespond}
            onClickCadence={onClickCadence}
            atVariables={atVariables}
            onMarkMeetingBooked={mutations.markMeetingBooked}
            isMarkingMeetingBooked={mutations.isMarkingMeetingBooked}
            onRetryAttachment={mutations.retryAttachment}
            retryStates={retryStates}
            retryErrors={retryErrors}
            onRequestTranscription={onRequestTranscription}
            transcriptionRequestStates={transcriptionRequestStates}
            cadenceStepsGraph={cadenceStepsGraph}
            cadenceTemplateInfo={cadenceTemplateInfo}
            cadenceDocuments={cadenceDocuments}
            onCadenceCreateStep={onCadenceCreateStep}
            onCadenceUpdateStep={onCadenceUpdateStep}
            onCadenceDeleteStep={onCadenceDeleteStep}
            onCadenceChangeStepType={onCadenceChangeStepType}
            onCadenceMarkStepFinished={onCadenceMarkStepFinished}
            onToggleInmailOpen={onToggleInmailOpen}
            renderSiftConditionBuilder={renderSiftConditionBuilder}
            reminderActions={reminderActions}
            reminderActionsLoading={reminderActionsLoading}
            onCreateReminder={onCreateReminder}
            reminderCreating={reminderCreating}
            onReminderError={onReminderError}
            voiceEnabled={voiceEnabled}
            voiceCallingEnabled={voiceCallingEnabled}
            renderCadenceCallButton={renderCadenceCallButton}
            onMarkCallStepDone={onMarkCallStepDone}
            templateLinkBaseUrl={templateLinkBaseUrl}
          />
        )}

        {/* Data Tab */}
        {activeTab === 'data' && (
          <>
          <ContactDataSectionPure
            contact={contact}
            authUserId={authUserId}
            authIsPremium={authIsPremium ?? false}
            authCreditsRemaining={authCreditsRemaining}
            authUnipileAccountId={authUnipileAccountId}
            authContactDisqualified={data.authedUser.disqualified}
            meetingBookedDetails={meetingBookedDetails}
            pendingAction={pendingAction}
            hasSteps={hasSteps}
            hasCadence={hasCadence}
            onToggleTracking={mutations.setTracking}
            onToggleDisqualified={mutations.setDisqualified}
            onAddFieldValue={mutations.updateField}
            onDeleteFieldValue={mutations.deleteFieldValue}
            onMakeFieldPrimary={mutations.makeFieldPrimary}
            onRemoveFieldPrimary={mutations.removeFieldPrimary}
            onVerifyEmail={mutations.verifyEmail}
            onRefreshFromLinkedIn={mutations.refreshFromLinkedIn}
            onEnhanceContact={mutations.enhanceContact}
            onDeleteMeetingBooked={mutations.deleteMeetingBooked}
            onAgeOverride={mutations.setAgeOverride}
            onResetGender={mutations.resetGender}
            isRefreshing={mutations.isRefreshing}
            isEnhancing={mutations.isEnhancing}
            isTrackingPending={mutations.isTrackingPending}
            isDisqualifiedPending={mutations.isDisqualifiedPending}
            isAgeOverridePending={mutations.isAgeOverridePending}
            isResetGenderPending={mutations.isResetGenderPending}
            onFieldCopied={onFieldCopied}
            onFieldCopyFailed={onFieldCopyFailed}
            linkedInMappings={linkedInMappings}
            linkedinUnavailable={linkedinUnavailable}
            isLinkedInMappingsLoading={isLinkedInMappingsLoading}
            onDeleteLinkedInMapping={onDeleteLinkedInMapping}
            onVerifyLinkedInMapping={onVerifyLinkedInMapping}
            onCopyLinkedInMapping={onCopyLinkedInMapping}
            onAddLinkedInMapping={onAddLinkedInMapping}
            isLinkedInMappingDeleting={isLinkedInMappingDeleting}
            isLinkedInMappingVerifying={isLinkedInMappingVerifying}
            isLinkedInMappingAdding={isLinkedInMappingAdding}
            onCopyToSuperadminTeam={onCopyToSuperadminTeam}
            isCopyPending={isCopyPending}
            superadminName={superadminName}
            superadminTeamName={superadminTeamName}
            onConnectUnipile={onUnipileConnect}
            onViewAllCampaigns={onViewAllCampaigns}
            onViewRequiredActions={onViewRequiredActions}
            renderContactTags={renderContactTags}
            signals={signals}
            isSignalsLoading={isSignalsLoading}
            lastUnipileFetchAt={lastUnipileFetchAt}
            ignoredAspectKeys={ignoredAspectKeys}
            experiences={experiences}
            education={education}
            isHistoryLoading={isHistoryLoading}
            onAddExperience={onAddExperience}
            orgSearch={orgSearch}
            onDeleteExperience={onDeleteExperience}
          />
          </>
        )}

        {/* Dossier Tab — lives next to Data so reps can drop into
            the long research write-up without scrolling past Contact
            Info every time. Plain Scrollable here (not SmallPadded)
            because ContactDossierSection brings its own `px-4 py-3`,
            and SmallPadded's `p-4` was stacking on top to produce
            ~32px of horizontal padding. */}
        {activeTab === 'dossier' && !hideDossierTab && (
          <Scrollable>
            <ContactDossierSection
              dossier={dossier}
              isGenerating={isDossierGenerating}
              hasTavilyKey={hasTavilyKey}
              onSaveTavilyKey={onSaveTavilyKey ?? (async () => undefined)}
              onRegenerateDossier={onRegenerateDossier ?? (async () => undefined)}
              isRegeneratingDossier={isRegeneratingDossier}
              failure={dossierFailure}
            />
          </Scrollable>
        )}

        {/* Respond Section */}
        {respondStep != null && onRespondSubmit && onRespondChange && (
          <CadenceStepSectionPure
            userTeam={{
              teamId: authTeamId,
              userId: authUserId,
              isPremium: authIsPremium ?? false,
              useEmailSignatureByDefault: false,
              useContactEmailAsBcc: false,
            }}
            contact={{ id: contact?.id ?? '', primaryFields: contact?.primaryFields ?? {} }}
            contactUser={{
              liConnectedAt: data.authedUser?.liConnectedAt ?? null,
              emailCredentialId: data.authedUser?.emailCredentialId ?? null,
            }}
            step={respondStep}
            atVariables={atVariables}
            documents={respondDocuments}
            fromEmailOptions={[]}
            toEmailOptions={[]}
            threadOptions={[]}
            aiChoices={respondAiChoices}
            aiIndex={respondAiIndex}
            aiCommand={respondAiCommand}
            aiLoading={respondAiLoading}
            stepsList={respondStepsList}
            allowEditStepType={false}
            allowSaveAsTemplate={false}
            allowEmailDetails={false}
            disableLikeRecentPosts
            texts={{ submit: 'Respond' }}
            isSubmitting={respondIsSubmitting}
            isAiGenerating={respondIsAiGenerating}
            onSubmit={onRespondSubmit}
            onCancel={onCancelRespond}
            onChange={onRespondChange}
            onGenerateAi={onRespondGenerateAi}
            onAiCommandChange={onRespondAiCommandChange}
            onAiIndexChange={onRespondAiIndexChange}
            onLoadThreads={async () => {}}
            onToast={onRespondToast}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Skeleton Components
// ============================================================================

